import { ExpandMore } from "@mui/icons-material";
import { Box, Button, Collapse, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import FormTemplateFormDisplay from "~/shared/components/FormTemplate/FormTemplateFormDisplay";
import { createFieldList } from "~/shared/components/FormTemplate/formHelpers";
import { Shared_FormTemplateFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";

type Props = {
    formTemplate: Shared_FormTemplateFragment;
    answers: Record<string, string | null> | null;
};

export default function ObservationCardAnswers({ formTemplate, answers }: Props) {
    const { t } = useTranslation("mapEditor");
    const [isExpanded, setIsExpanded] = useState(false);

    const values = answers ?? {};
    const answeredQuestions = createFieldList(formTemplate, values).filter(question => Boolean(values[question.key]));

    if (answeredQuestions.length === 0) {
        return null;
    }

    const answeredForm = { ...formTemplate, questions: answeredQuestions };

    return (
        <Stack>
            <Button
                size="small"
                fullWidth
                aria-expanded={isExpanded}
                onMouseDown={event => event.stopPropagation()}
                onClick={event => {
                    event.preventDefault();
                    setIsExpanded(previous => !previous);
                }}
                endIcon={
                    <ExpandMore
                        sx={{
                            transition: theme => theme.transitions.create("transform"),
                            transform: isExpanded ? "rotate(180deg)" : "none",
                        }}
                    />
                }
                sx={{ justifyContent: "space-between" }}
            >
                {isExpanded
                    ? t("observationCard.btnHideDetails", { count: answeredQuestions.length })
                    : t("observationCard.btnShowDetails", { count: answeredQuestions.length })}
            </Button>
            <Collapse in={isExpanded} timeout="auto" sx={{ width: "100%" }}>
                {/* Note: The card can be wrapped in a link, so keep clicks here from navigating to allow selecting and copying answers */}
                <Box
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.preventDefault()}
                    sx={{
                        marginTop: 0.5,
                        padding: 1.5,
                        borderRadius: 1,
                        backgroundColor: "action.hover",
                        cursor: "text",
                        userSelect: "text",
                    }}
                >
                    <FormTemplateFormDisplay form={answeredForm} values={values} />
                </Box>
            </Collapse>
        </Stack>
    );
}
